import { useMutation } from '@apollo/client/react';
import AddReactionIcon from '@mui/icons-material/AddReaction';
import EditIcon from '@mui/icons-material/Edit';
import ReplyIcon from '@mui/icons-material/Reply';
import { Avatar, Chip, Grid, IconButton, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import DOMPurify from 'dompurify';
import { map } from 'lodash';
import React, { useContext } from 'react';

import CurrentUserContext from '@/Contexts/CurrentUser';
import { type TaskComment, type CommentReaction, ReactOnTaskCommentDocument } from '@/graphql/generated/graphql';
import { EMOJIS } from '@/utils/constants';
import { objectLength } from '@/utils/helperFunctions';

interface CommentContentProps {
  comment: TaskComment;
  onReplyClick: () => void;
  onEditClick: () => void;
  getReactorsInfo: (reaction: CommentReaction) => JSX.Element;
  isReply?: boolean;
}

const CommentContent: React.FC<CommentContentProps> = ({
  comment,
  onReplyClick,
  onEditClick,
  getReactorsInfo,
  isReply = false,
}) => {
  const { currentUser } = useContext(CurrentUserContext);
  const sanitizedContent = DOMPurify.sanitize(comment.content as string); // sanitization process

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const [reactOnComment] = useMutation(ReactOnTaskCommentDocument, {
    refetchQueries: ['getTaskComments', 'getTasks'],
  });

  const handleClickReaction = (emo: string, commentId: string) => {
    reactOnComment({
      variables: {
        reaction: {
          commentId: commentId,
          emoji: emo,
        },
      },
    });

    handleClose();
  };

  return (
    <Grid
      container
      size={12}
      mt={2}
      justifyContent="space-between"
      sx={{
        paddingLeft: 0,
      }}
    >
      {isReply && (
        <Grid size={0.5}>
          <hr />
        </Grid>
      )}
      <Grid container justifyContent="space-between">
        <Avatar src={comment.employment.employee?.imageUrl as string} sx={{ width: 35, height: 35 }} />
      </Grid>

      <Grid size={isReply ? 10 : 11}>
        <Typography
          variant="body2"
          sx={{
            mr: 'auto',
            '& > :first-child': {
              marginTop: 1,
            },
          }}
          dangerouslySetInnerHTML={{ __html: sanitizedContent }}
        />

        <Grid container spacing={1} mt={1}>
          <Grid container>
            {comment.isEdited && <Chip label="Edited" />}

            <Tooltip title="Reply to the comment">
              <ReplyIcon color="primary" onClick={onReplyClick} sx={{ cursor: 'pointer' }} />
            </Tooltip>

            {currentUser?.id === comment.employment.employee?.id && (
              <Tooltip title="Edit Comment">
                <EditIcon color="primary" onClick={onEditClick} sx={{ cursor: 'pointer' }} />
              </Tooltip>
            )}

            <AddReactionIcon
              onClick={(event) => handleClick(event as unknown as React.MouseEvent<HTMLElement>)}
              id="emojis-button"
              sx={{ cursor: 'pointer' }}
              color="primary"
            />
            <Menu
              id="emojis-menu"
              aria-labelledby="emojis-button"
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
              <MenuItem>
                {map(EMOJIS, (emo) => (
                  <IconButton onClick={() => handleClickReaction(emo, comment.id)} key={`task-comment-emoji-${emo}`}>
                    {emo}
                  </IconButton>
                ))}
              </MenuItem>
            </Menu>
          </Grid>

          <Grid>
            {map(comment.reactions || [], (reaction) => (
              <IconButton
                key={`reaction-chip-${comment.id}-${reaction.emoji}`}
                onClick={() => handleClickReaction(reaction.emoji, comment.id)}
                size="small"
              >
                <Tooltip title={getReactorsInfo(reaction)}>
                  <Chip size="small" label={`${reaction.emoji} ${objectLength(reaction.employments)}`} />
                </Tooltip>
              </IconButton>
            ))}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default CommentContent;
